import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Checkbox, Input, InputNumber, Slider, Spin, message } from 'antd';
import {
    DeliveredProcedureOutlined,
    FilterOutlined,
    LeftOutlined,
    PrinterOutlined,
    QrcodeOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../../config';
import ozonOrdersStore from '../../stores/OzonOrdersStore';
import { applyNameReplacements } from '../../utils/nameReplacements';
import {
    QR_SEARCH_FORMATS,
    startCameraBarcodeScanner,
} from '../Scanner/cameraBarcodeScanner';
import './OzoneOrdersMobile.css';

const STATUS_LABELS = {
    awaiting_packaging: 'Ожидает сборки',
    awaiting_deliver: 'Готов к отгрузке',
    delivering: 'Доставляется',
    delivered: 'Доставлен',
    cancelled: 'Отменён',
};

const MOBILE_FILTERS = [
    { key: 'packaging', label: 'Ожидают сборки' },
    { key: 'current', label: 'Текущая работа' },
    { key: 'active', label: 'Активные' },
    { key: 'shipping', label: 'Готовы к отгрузке' },
    { key: 'cancelled', label: 'Отменены' },
    { key: 'delivering', label: 'Доставляются' },
    { key: 'all', label: 'Все' },
];

const MOBILE_SETTINGS_KEY = 'ozonOrdersMobileSettings';
const cleanPostingNumber = (value) => String(value || '').replace(/\s+/g, '');

const getProductImage = (product) =>
    product?.primary_image ||
    product?.image ||
    product?.image_url ||
    product?.color?.image ||
    '';

const getOrderPrice = (order) =>
    (order.products || []).reduce(
        (total, product) =>
            total + Number(product.price || product.currency_price || product.price_commission || 0),
        0
    );

const OzoneOrdersMobile = observer(() => {
    const { orders, loading, filters, quickFilter, quickFilterCounts, isLabelPrintLocked, labelPrintCountdown, labelTimerRemaining } = ozonOrdersStore;
    const [query, setQuery] = useState('');
    const [collecting, setCollecting] = useState({});
    const [printing, setPrinting] = useState({});
    const [productTitleSize, setProductTitleSize] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(MOBILE_SETTINGS_KEY) || '{}');
            const size = Number(saved.productTitleSize);
            return Number.isFinite(size) && size >= 10 && size <= 20 ? size : 16;
        } catch {
            return 16;
        }
    });
    const [showProductImage, setShowProductImage] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(MOBILE_SETTINGS_KEY) || '{}');
            return saved.showProductImage !== false;
        } catch {
            return true;
        }
    });
    const [controlScale, setControlScale] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(MOBILE_SETTINGS_KEY) || '{}');
            const scale = Number(saved.controlScale);
            return Number.isFinite(scale) && scale >= 0.4 && scale <= 1.4 ? scale : 1;
        } catch {
            return 1;
        }
    });
    const [filterScale, setFilterScale] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(MOBILE_SETTINGS_KEY) || '{}');
            const scale = Number(saved.filterScale);
            return Number.isFinite(scale) && scale >= 0.4 && scale <= 1.4 ? scale : 1;
        } catch {
            return 1;
        }
    });
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [nameReplacements, setNameReplacements] = useState([]);
    const [qrScannerOpen, setQrScannerOpen] = useState(false);
    const qrVideoRef = useRef(null);
    const qrHandledRef = useRef(false);

    useEffect(() => {
        try {
            localStorage.setItem(
                MOBILE_SETTINGS_KEY,
                JSON.stringify({
                    productTitleSize,
                    showProductImage,
                    controlScale,
                    filterScale,
                })
            );
        } catch {
            // Настройка необязательна, если хранилище браузера недоступно.
        }
    }, [controlScale, filterScale, productTitleSize, showProductImage]);

    useEffect(() => {
        ozonOrdersStore.ensureOrdersLoaded();
        ozonOrdersStore.startStatusPolling();
        return () => ozonOrdersStore.stopStatusPolling();
    }, []);

    useEffect(() => {
        const fetchNameReplacements = async () => {
            try {
                const token = localStorage.getItem('token');
                const { data } = await axios.get(
                    `${API_URL}/v1/Settings/getProductNameReplacements`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setNameReplacements(Array.isArray(data?.items) ? data.items : []);
            } catch (error) {
                console.error('Error fetching product name replacements:', error);
                setNameReplacements([]);
            }
        };
        fetchNameReplacements();
    }, []);

    useEffect(() => {
        if (!qrScannerOpen) return undefined;

        let cancelled = false;
        let stopScanner = () => {};
        qrHandledRef.current = false;

        const start = () => {
            const video = qrVideoRef.current;
            if (!video) {
                if (!cancelled) window.requestAnimationFrame(start);
                return;
            }

            void startCameraBarcodeScanner({
                video,
                formats: QR_SEARCH_FORMATS,
                onDetect: (text) => {
                    if (cancelled || qrHandledRef.current) return;
                    const value = String(text || '').trim();
                    if (!value) return;
                    qrHandledRef.current = true;
                    setQuery(value);
                    ozonOrdersStore.setQuickFilter('all');
                    setQrScannerOpen(false);
                    message.success({ content: 'QR-код отсканирован', duration: 2 });
                },
                onError: (error) => {
                    if (cancelled) return;
                    message.error({
                        content: error.message,
                        duration: 3,
                    });
                    setQrScannerOpen(false);
                },
            }).then((stop) => {
                if (cancelled) {
                    stop();
                    return;
                }
                stopScanner = stop;
            });
        };

        start();
        return () => {
            cancelled = true;
            stopScanner();
        };
    }, [qrScannerOpen]);

    const filteredOrders = useMemo(() => {
        const needle = cleanPostingNumber(query).toLowerCase();
        if (!needle) return orders;
        return orders.filter((order) =>
            cleanPostingNumber(order.posting_number).toLowerCase().includes(needle)
        );
    }, [orders, query]);

    const handleCollect = async (postingNumber) => {
        setCollecting((current) => ({ ...current, [postingNumber]: true }));
        try {
            const result = await ozonOrdersStore.collectOrder(postingNumber);
            message.success(
                ozonOrdersStore.isLabelPrintLockedFor(postingNumber)
                    ? `${result.message}. Печать через ${ozonOrdersStore.labelPrintCountdownFor(postingNumber)}`
                    : result.message
            );
        } catch (error) {
            message.error(error.response?.data?.message || error.message || 'Не удалось собрать заказ');
        } finally {
            setCollecting((current) => ({ ...current, [postingNumber]: false }));
        }
    };

    const handlePrint = async (postingNumber) => {
        if (ozonOrdersStore.isLabelPrintLockedFor(postingNumber)) {
            message.warning(`Печать будет доступна через ${ozonOrdersStore.labelPrintCountdownFor(postingNumber)}`);
            return;
        }
        setPrinting((current) => ({ ...current, [postingNumber]: true }));
        try {
            const result = await ozonOrdersStore.printPostingLabel(postingNumber);
            if (result.warning) {
                message.warning(result.message, 8);
            } else {
                message.success('Этикетка загружена');
            }
        } catch (error) {
            message.error(error.response?.data?.message || error.message || 'Не удалось загрузить этикетку');
        } finally {
            setPrinting((current) => ({ ...current, [postingNumber]: false }));
        }
    };

    return (
        <main
            className="ozon-mobile-orders"
            style={{
                '--ozon-mobile-title-size': `${productTitleSize}px`,
                '--ozon-mobile-control-scale': controlScale,
                '--ozon-mobile-filter-scale': filterScale,
            }}
        >
            <section className="ozon-mobile-orders__controls">
                <div className="ozon-mobile-orders__search-row">
                    <Input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        prefix={<SearchOutlined />}
                        placeholder="Номер заказа"
                        className="ozon-mobile-orders__search"
                        allowClear
                    />
                    <button
                        type="button"
                        className="ozon-mobile-orders__icon-button"
                        title="Сканер штрихкодов"
                        aria-label="Сканер штрихкодов"
                        onClick={() => setQrScannerOpen(true)}
                    >
                        <QrcodeOutlined />
                    </button>
                    <button
                        type="button"
                        className="ozon-mobile-orders__icon-button"
                        title="Настройки отображения"
                        aria-label="Настройки отображения"
                        onClick={() => setSettingsOpen(true)}
                    >
                        <FilterOutlined />
                    </button>
                </div>

                <div className="ozon-mobile-orders__filters" role="tablist" aria-label="Фильтры заказов">
                    {MOBILE_FILTERS.map((filter) => {
                        const active = quickFilter === filter.key;
                        return (
                            <button
                                key={filter.key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                className={`ozon-mobile-orders__filter${
                                    active ? ' ozon-mobile-orders__filter--active' : ''
                                }`}
                                onClick={() => ozonOrdersStore.setQuickFilter(filter.key)}
                            >
                                <span>{filter.label}</span>
                                <span className="ozon-mobile-orders__filter-count">
                                    {quickFilterCounts?.[filter.key] ?? 0}
                                </span>
                            </button>
                        );
                    })}
                </div>
                {isLabelPrintLocked && (
                    <div className="ozon-mobile-label-timer">
                        Печать этикеток через {labelPrintCountdown}
                    </div>
                )}
            </section>

            <section className="ozon-mobile-orders__content">
                {loading ? (
                    <div className="ozon-mobile-orders__loading">
                        <Spin size="large" />
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="ozon-mobile-orders__empty">Заказы не найдены</div>
                ) : (
                    filteredOrders.map((order) => {
                                const products = order.products || [];
                                const firstProduct = products[0] || {};
                                const image = getProductImage(firstProduct);
                                const price = getOrderPrice(order);
                                const postingNumber = cleanPostingNumber(order.posting_number);
                                return (
                                    <article key={order.order_id || postingNumber} className="ozon-mobile-order-card">
                                        <div className="ozon-mobile-order-card__top">
                                            <span
                                                className={`ozon-mobile-order-card__status ozon-mobile-order-card__status--${order.status}`}
                                            >
                                                {STATUS_LABELS[order.status] || order.status || 'Статус неизвестен'}
                                            </span>
                                            <span className="ozon-mobile-order-card__posting">{postingNumber || '—'}</span>
                                            {price > 0 && (
                                                <span className="ozon-mobile-order-card__price-badge">
                                                    {Math.round(price).toLocaleString('ru-RU')} ₽
                                                </span>
                                            )}
                                        </div>

                                        <div
                                            className={`ozon-mobile-order-card__products${
                                                showProductImage
                                                    ? ''
                                                    : ' ozon-mobile-order-card__products--without-image'
                                            }`}
                                        >
                                            {showProductImage &&
                                                (image ? (
                                                    <img src={image} alt="" className="ozon-mobile-order-card__image" />
                                                ) : (
                                                    <div className="ozon-mobile-order-card__image-placeholder">
                                                        {String(firstProduct.offer_id || 'O').slice(0, 1)}
                                                    </div>
                                                ))}
                                            <div className="ozon-mobile-order-card__product-copy">
                                                <div className="ozon-mobile-order-card__product-title">
                                                    {products.length > 1
                                                        ? `${products.length} артикула, ${products.reduce(
                                                              (sum, product) =>
                                                                  sum + Number(product.quantity || product.present || 1),
                                                              0
                                                          )} экземпляра`
                                                        : applyNameReplacements(
                                                              firstProduct.color?.shortName ||
                                                                  firstProduct.shortName ||
                                                                  firstProduct.name ||
                                                                  firstProduct.offer_id ||
                                                                  'Товар',
                                                              nameReplacements
                                                          )}
                                                </div>
                                                <div className="ozon-mobile-order-card__article">
                                                    {products
                                                        .map(
                                                            (product) =>
                                                                product.offer_id ||
                                                                product?.color?._id ||
                                                                product.shortName
                                                        )
                                                        .filter(Boolean)
                                                        .join(', ') || '—'}
                                                </div>
                                            </div>
                                            {products.length === 1 && (
                                                <span className="ozon-mobile-order-card__quantity">
                                                    {firstProduct.quantity || firstProduct.present || 1} шт
                                                </span>
                                            )}
                                        </div>

                                        {order.status === 'awaiting_packaging' && (
                                            <Button
                                                className="ozon-mobile-order-card__action"
                                                type="primary"
                                                icon={<DeliveredProcedureOutlined />}
                                                loading={collecting[order.posting_number]}
                                                onClick={() => handleCollect(order.posting_number)}
                                                block
                                            >
                                                Собрать
                                            </Button>
                                        )}
                                        {order.status === 'awaiting_deliver' && (
                                            <Button
                                                className="ozon-mobile-order-card__action"
                                                type="primary"
                                                icon={<PrinterOutlined />}
                                                loading={printing[order.posting_number]}
                                                disabled={(labelTimerRemaining[postingNumber] || 0) > 0}
                                                title={
                                                    (labelTimerRemaining[postingNumber] || 0) > 0
                                                        ? `Печать будет доступна через ${ozonOrdersStore.labelPrintCountdownFor(postingNumber)}`
                                                        : undefined
                                                }
                                                onClick={() => handlePrint(order.posting_number)}
                                                block
                                            >
                                                {(labelTimerRemaining[postingNumber] || 0) > 0
                                                    ? `Печать через ${ozonOrdersStore.labelPrintCountdownFor(postingNumber)}`
                                                    : 'Печать'}
                                            </Button>
                                        )}
                                    </article>
                                );
                            })
                )}
            </section>
            {settingsOpen && (
                <div className="ozon-mobile-settings" role="dialog" aria-modal="true" aria-labelledby="ozon-mobile-settings-title">
                    <header className="ozon-mobile-settings__header">
                        <button
                            type="button"
                            className="ozon-mobile-orders__icon-button"
                            onClick={() => setSettingsOpen(false)}
                            aria-label="Назад"
                        >
                            <LeftOutlined />
                        </button>
                        <h1 id="ozon-mobile-settings-title">Настройки</h1>
                    </header>
                    <div className="ozon-mobile-settings__body">
                        <section className="ozon-mobile-settings__card">
                            <h2>Период заказов</h2>
                            <div className="ozon-mobile-orders__period-presets">
                                {[3, 7, 14, 30].map((days) => (
                                    <button
                                        key={days}
                                        type="button"
                                        className={Number(filters.hist) === days ? 'is-active' : ''}
                                        onClick={() => ozonOrdersStore.setFilters({ hist: days })}
                                    >
                                        {days} дн.
                                    </button>
                                ))}
                            </div>
                            <div className="ozon-mobile-orders__period-custom">
                                <span>Свой период:</span>
                                <InputNumber
                                    min={1}
                                    max={365}
                                    precision={0}
                                    value={Number(filters.hist) || 3}
                                    onChange={(days) => {
                                        if (Number.isFinite(days) && days >= 1) {
                                            ozonOrdersStore.setFilters({ hist: Math.floor(days) });
                                        }
                                    }}
                                />
                                <span>дн.</span>
                            </div>
                        </section>
                        <section className="ozon-mobile-settings__card">
                            <h2>Отображение</h2>
                            <div className="ozon-mobile-settings__row">
                                <div>Название товара: {productTitleSize}px</div>
                                <Slider
                                    min={10}
                                    max={20}
                                    value={productTitleSize}
                                    onChange={setProductTitleSize}
                                />
                            </div>
                            <div className="ozon-mobile-settings__check">
                                <Checkbox
                                    checked={showProductImage}
                                    onChange={(event) => setShowProductImage(event.target.checked)}
                                >
                                    Показывать фото товара
                                </Checkbox>
                            </div>
                            <div className="ozon-mobile-settings__row">
                                <div>Масштаб кнопок и статусов: {Math.round(controlScale * 100)}%</div>
                                <Slider
                                    min={0.4}
                                    max={1.4}
                                    step={0.05}
                                    value={controlScale}
                                    onChange={setControlScale}
                                />
                            </div>
                            <div className="ozon-mobile-settings__row">
                                <div>Масштаб меню фильтров: {Math.round(filterScale * 100)}%</div>
                                <Slider
                                    min={0.4}
                                    max={1.4}
                                    step={0.05}
                                    value={filterScale}
                                    onChange={setFilterScale}
                                />
                            </div>
                        </section>
                        <section className="ozon-mobile-settings__preview">
                            <h2>Пример</h2>
                            <div className="ozon-mobile-orders__filters ozon-mobile-settings__preview-filters" aria-hidden="true">
                                <span className="ozon-mobile-orders__filter ozon-mobile-orders__filter--active">
                                    <span>Ожидают сборки</span>
                                    <span className="ozon-mobile-orders__filter-count">12</span>
                                </span>
                                <span className="ozon-mobile-orders__filter">
                                    <span>Все</span>
                                    <span className="ozon-mobile-orders__filter-count">48</span>
                                </span>
                            </div>
                            <article className="ozon-mobile-order-card ozon-mobile-settings__preview-card">
                                <div className="ozon-mobile-order-card__top">
                                    <span className="ozon-mobile-order-card__status">Ожидает сборки</span>
                                    <span className="ozon-mobile-order-card__posting">12345678-0001-1</span>
                                    <span className="ozon-mobile-order-card__price-badge">1 290 ₽</span>
                                </div>
                                <div
                                    className={`ozon-mobile-order-card__products${
                                        showProductImage ? '' : ' ozon-mobile-order-card__products--without-image'
                                    }`}
                                >
                                    {showProductImage && (
                                        <img
                                            src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 72 72'%3E%3Crect width='72' height='72' fill='%23f6e7b4'/%3E%3Cellipse cx='36' cy='38' rx='16' ry='22' fill='%23f4c430'/%3E%3Cpath d='M36 12c6 4 8 10 8 16' fill='none' stroke='%237a5b12' stroke-width='4' stroke-linecap='round'/%3E%3C/svg%3E"
                                            alt=""
                                            className="ozon-mobile-order-card__image"
                                        />
                                    )}
                                    <div className="ozon-mobile-order-card__product-copy">
                                        <div className="ozon-mobile-order-card__product-title">
                                            Банан сушеный, 100 г
                                        </div>
                                        <div className="ozon-mobile-order-card__article">BN-100-GOLD</div>
                                    </div>
                                    <span className="ozon-mobile-order-card__quantity">2 шт</span>
                                </div>
                                <Button
                                    className="ozon-mobile-order-card__action"
                                    type="primary"
                                    icon={<DeliveredProcedureOutlined />}
                                    block
                                    tabIndex={-1}
                                >
                                    Собрать
                                </Button>
                            </article>
                        </section>
                    </div>
                </div>
            )}
            {qrScannerOpen && (
                <div className="ozon-mobile-qr-scanner" role="dialog" aria-modal="true">
                    <div className="ozon-mobile-qr-scanner__top">
                        <span>Наведите камеру на QR-код</span>
                        <button
                            type="button"
                            onClick={() => setQrScannerOpen(false)}
                            aria-label="Закрыть сканер"
                        >
                            Закрыть
                        </button>
                    </div>
                    <div className="ozon-mobile-qr-scanner__viewport">
                        <video ref={qrVideoRef} muted playsInline />
                        <div className="ozon-mobile-qr-scanner__frame" aria-hidden="true" />
                    </div>
                    <p>QR-код будет подставлен в поиск автоматически</p>
                </div>
            )}
        </main>
    );
});

export default OzoneOrdersMobile;